import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/apiConfig";

const POLL_INTERVAL_MS = 30000;
const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const MaintenanceContext = createContext(null);

const toMethod = (method) => (method || "GET").toString().toUpperCase();

const buildMaintenanceError = (state) => {
  const error = new Error(state.message || "Scheduled maintenance in progress.");
  error.isMaintenanceMode = true;
  error.status = 503;
  error.maintenance = state;
  return error;
};

export const useMaintenance = () => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error("useMaintenance must be used within a MaintenanceProvider");
  }
  return context;
};

export const MaintenanceProvider = ({ children }) => {
  const [state, setState] = useState({
    maintenanceMode: false,
    message: "",
    until: "",
    lastCheckedAt: null,
  });
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const applyStatusPayload = useCallback((payload) => {
    if (!payload || typeof payload !== "object") {
      return;
    }

    setState((prev) => ({
      ...prev,
      maintenanceMode: Boolean(payload.maintenance_mode),
      message: payload.message || "",
      until: payload.until || "",
      lastCheckedAt: new Date().toISOString(),
    }));
  }, []);

  const loadMaintenanceStatus = useCallback(async () => {
    try {
      const response = await window.fetch(API_ENDPOINTS.maintenanceStatus, { method: "GET" });
      if (!response.ok) {
        return;
      }
      const payload = await response.json();
      applyStatusPayload(payload);
    } catch (error) {
      // Keep last known state if polling fails temporarily.
    }
  }, [applyStatusPayload]);

  useEffect(() => {
    let disposed = false;

    const poll = async () => {
      if (!disposed) {
        await loadMaintenanceStatus();
      }
    };

    poll();
    const intervalId = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      disposed = true;
      window.clearInterval(intervalId);
    };
  }, [loadMaintenanceStatus]);

  useEffect(() => {
    const requestInterceptorId = axios.interceptors.request.use(
      (config) => {
        const current = stateRef.current;
        const method = toMethod(config?.method);
        if (current.maintenanceMode && WRITE_METHODS.has(method)) {
          return Promise.reject(buildMaintenanceError(current));
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const data = error?.response?.data;
        if (status === 503 && data?.maintenance) {
          applyStatusPayload({
            maintenance_mode: true,
            message: data.message || "",
            until: data.until || "",
          });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, [applyStatusPayload]);

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (input, init = {}) => {
      const explicitMethod = init?.method ? toMethod(init.method) : "";
      const requestMethod =
        explicitMethod ||
        (typeof Request !== "undefined" && input instanceof Request ? toMethod(input.method) : "GET");
      const current = stateRef.current;

      if (current.maintenanceMode && WRITE_METHODS.has(requestMethod)) {
        return new Response(
          JSON.stringify({
            detail: "Service temporarily unavailable",
            maintenance: true,
            message: current.message || "",
            until: current.until || "",
          }),
          {
            status: 503,
            headers: {
              "Content-Type": "application/json",
              "Retry-After": "60",
            },
          }
        );
      }

      const response = await originalFetch(input, init);

      if (response.status === 503) {
        try {
          const payload = await response.clone().json();
          if (payload?.maintenance) {
            applyStatusPayload({
              maintenance_mode: true,
              message: payload.message || "",
              until: payload.until || "",
            });
          }
        } catch (error) {
          // Non-JSON 503 responses are ignored.
        }
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [applyStatusPayload]);

  const value = {
    ...state,
    refresh: loadMaintenanceStatus,
  };

  return <MaintenanceContext.Provider value={value}>{children}</MaintenanceContext.Provider>;
};
